import React from 'react';
import CountUp from 'react-countup';
import styled from 'styled-components';
import { solarGradient, successGradient, white } from '../theme/colors';
import cx from 'classnames';
import PropTypes from 'prop-types';

const StyledHeader = styled.div`
  position: absolute;
  top: -66px;
  width: 100%;
  display: flex;
  white-space: nowrap;

  &.interactive {
    top: -40px;
  }

  .section-wrapper {
    text-align: center;

    .info-wrapper {
      min-height: 23px;

      .info {
        position: relative;
        top: 2px;
        display: inline;
        border-top-right-radius: 30px;
        border-top-left-radius: 30px;
        background: rgba(255, 255, 255, 0.3);
        color: ${white};
        font-size: 15px;
      }
    }

    .capsule-wrapper {
      background: rgba(255, 255, 255, 0.3);
      border-radius: 45px;
      padding: 10px;

      .capsule {
        border-radius: 35.5px;
        border: solid 3px ${white};
        color: ${white};
        text-align: center;
      }
    }

    &.emissions {
      width: 35%;
      padding: 0 10px 0 30px;

      .title {
        padding: 2px 12px 0;
      }

      .total {
        background-image: ${successGradient};
        font-size: 30px;
        padding: 8px;

        .tonnes {
          font-size: 22px;
        }
      }
    }

    &.label {
      width: 65%;
      padding: 0 30px 0 10px;

      .step {
        padding: 2px 20px 0;
      }

      .text {
        font-size: 20px;
        font-weight: 700;
        background: ${solarGradient};
        padding: 16px;
      }
    }
  }

  @media screen and (max-width: 780px) {
    white-space: normal;
    top: -72px;

    &.interactive {
      top: -20px;
    }

    .section-wrapper {
      .info-wrapper {
        .info {
          top: 1px;
          white-space: nowrap;
          border-radius: 20px;
          padding: 1px 10px;
        }
      }

      .capsule-wrapper {
        padding: 8px;

        .capsule {
          border-width: 2px;
          border-radius: 40px;
          min-height: 80px;
          align-items: center;
          justify-content: center;
          display: flex;
          flex-direction: column;
          padding: 5px;
        }
      }

      &.emissions {
        width: 40%;
        padding-left: 12px;

        .total {
          line-height: 16px;

          strong {
            font-size: 22px;
            line-height: 22px;
          }

          .tonnes {
            font-size: 14px;
          }
        }
      }

      &.label {
        width: 60%;
        padding-right: 12px;

        .text {
          font-size: 16px;
          line-height: 16px;
          padding: 12px;
        }
      }
    }
  }

  @media screen and (max-width: 420px) {
    .section-wrapper {
      .info-wrapper {
        .info {
          font-size: 10px;
          top: 2px;
        }
      }

      &.emissions {
        padding-left: 6px;

        .total {
          strong {
            font-size: 22px;
            line-height: 22px;
          }

          .tonnes {
            font-size: 12px;
          }
        }
      }

      &.label {
        padding-right: 6px;

        .text {
          font-size: 12px;
          line-height: 12px;
          padding: 6px;
        }
      }
    }
  }
`;

const NetZeroHeader = ({
  prevEmissions,
  emissions,
  duration,
  adjustedCount,
  currentContent,
  interactive,
}) => (
  <StyledHeader className={cx({ interactive: interactive })}>
    <div className="section-wrapper emissions">
      <div className="info-wrapper">
        <div className="info title">CO2 emissions</div>
      </div>
      <div className="capsule-wrapper">
        <div className="capsule total">
          <strong>
            <CountUp
              start={prevEmissions}
              end={emissions}
              duration={duration}
              decimals={1}
            />
          </strong>{' '}
          <span className="tonnes">tonnes</span>
        </div>
      </div>
    </div>
    <div className="section-wrapper label">
      <div className="info-wrapper">
        {adjustedCount >= 1 && adjustedCount <= 4 && (
          <div className="info step">Step {adjustedCount} of 4</div>
        )}
      </div>
      <div className="capsule-wrapper">
        <div
          className="capsule text"
          style={{ background: currentContent.titleGradient }}>
          {currentContent.title}
        </div>
      </div>
    </div>
  </StyledHeader>
);

NetZeroHeader.propTypes = {
  prevEmissions: PropTypes.number.isRequired,
  emissions: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  adjustedCount: PropTypes.number.isRequired,
  currentContent: PropTypes.shape({
    titleGradient: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  interactive: PropTypes.bool.isRequired,
};

export default NetZeroHeader;
